// Laplacian kernel for second derivative (most reliable for blur detection)
const LAPLACIAN = [0, 1, 0, 1, -4, 1, 0, 1, 0];

export interface BlurResult {
  score: number; // 0-1, lower = more blurry (maintains original API contract)
  laplacianVariance: number; // Raw variance, lower = more blurry
  isBlurry: boolean; // Convenience flag
}

export async function measureBlur(imageSrc: string): Promise<number> {
  const result = await measureBlurDetailed(imageSrc);
  return result.score;
}

export async function measureBlurDetailed(
  imageSrc: string
): Promise<BlurResult> {
  const imageData = await getImageData(imageSrc);
  return processImage(imageData);
}

function getImageData(src: string): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = (): void => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(ctx.getImageData(0, 0, canvas.width, canvas.height));
    };
    img.onerror = reject;
    img.src = src;
  });
}

function processImage(imageData: ImageData): BlurResult {
  let pixels: Float32Array | Uint8ClampedArray = imageData.data;
  let width = imageData.width;
  let height = imageData.height;

  // Downsample large images for performance (keep aspect ratio)
  const maxDimension = 500;
  if (width > maxDimension || height > maxDimension) {
    const scale = maxDimension / Math.max(width, height);
    const downsampled = downsampleImage(pixels, width, height, scale);
    pixels = downsampled.data;
    width = downsampled.width;
    height = downsampled.height;
  }

  // Convert to grayscale
  const gray = convertToLuminance(pixels, width, height);

  // Apply Laplacian filter (second derivative - detects edges/sharpness)
  const laplacian = convolve(gray, width, height, LAPLACIAN);

  // Calculate variance of the Laplacian
  // High variance = sharp image (lots of edges with varying intensities)
  // Low variance = blurry image (smooth, few edges)
  const laplacianVariance = calculateVariance(laplacian);

  // Also check if image is too dark (pitch black) or too uniform
  const { mean: grayMean, variance: grayVariance } =
    calculateMeanAndVariance(gray);

  // Pitch black or very dark images have low gray mean AND low variance
  const isDark = grayMean < 15 && grayVariance < 100;
  // Uniform images (solid color) have very low variance
  const isUniform = grayVariance < 50;

  // Normalize the Laplacian variance to a 0-1 score
  // Based on empirical testing:
  // - Very blurry images: variance < 100
  // - Slightly blurry: variance 100-500
  // - Sharp images: variance > 500
  // - Very sharp images: variance > 1000

  let score: number;

  if (isDark || isUniform) {
    // Dark/uniform images are considered low quality
    score = 0.05;
  } else {
    // Normalize variance to 0-1 scale
    // Using a sigmoid-like curve for better discrimination
    const threshold = 100; // Below this is definitely blurry
    const sharpThreshold = 800; // Above this is definitely sharp

    if (laplacianVariance < threshold) {
      score = (laplacianVariance / threshold) * 0.25; // 0 to 0.25
    } else if (laplacianVariance < sharpThreshold) {
      // Linear interpolation between 0.25 and 1
      score =
        0.25 +
        ((laplacianVariance - threshold) / (sharpThreshold - threshold)) * 0.75;
    } else {
      score = 1.0;
    }
  }
  return {
    score,
    laplacianVariance,
    isBlurry: score < 0.25 || isDark || isUniform,
  };
}

function convertToLuminance(
  data: Float32Array | Uint8ClampedArray,
  width: number,
  height: number
): Float32Array {
  const output = new Float32Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const r = data[i * 4];
    const g = data[i * 4 + 1];
    const b = data[i * 4 + 2];
    output[i] = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }
  return output;
}

function convolve(
  data: Float32Array,
  width: number,
  height: number,
  kernel: number[]
): Float32Array {
  // Standard convolve on single channel (Luminance)
  const side = Math.round(Math.sqrt(kernel.length));
  const halfSide = Math.floor(side / 2);
  const output = new Float32Array(width * height);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const dstOff = y * width + x;
      let sum = 0;

      for (let cy = 0; cy < side; cy++) {
        for (let cx = 0; cx < side; cx++) {
          const sy = Math.min(height - 1, Math.max(0, y + cy - halfSide));
          const sx = Math.min(width - 1, Math.max(0, x + cx - halfSide));
          const srcOff = sy * width + sx;
          const wt = kernel[cy * side + cx];
          sum += data[srcOff] * wt;
        }
      }
      // Keep full range for Laplacian (can be negative), clamp for display
      output[dstOff] = sum;
    }
  }
  return output;
}

function calculateVariance(data: Float32Array): number {
  const { variance } = calculateMeanAndVariance(data);
  return variance;
}

function calculateMeanAndVariance(data: Float32Array): {
  mean: number;
  variance: number;
} {
  const n = data.length;
  if (n === 0) return { mean: 0, variance: 0 };

  let sum = 0;
  for (let i = 0; i < n; i++) {
    sum += data[i];
  }
  const mean = sum / n;

  let varianceSum = 0;
  for (let i = 0; i < n; i++) {
    const diff = data[i] - mean;
    varianceSum += diff * diff;
  }

  return { mean, variance: varianceSum / n };
}

function downsampleImage(
  pixels: Float32Array | Uint8ClampedArray,
  width: number,
  height: number,
  scale: number
): { data: Float32Array; width: number; height: number } {
  const newWidth = Math.floor(width * scale);
  const newHeight = Math.floor(height * scale);
  const output = new Float32Array(newWidth * newHeight * 4);

  for (let y = 0; y < newHeight; y++) {
    for (let x = 0; x < newWidth; x++) {
      const srcX = Math.floor(x / scale);
      const srcY = Math.floor(y / scale);
      const srcIdx = (srcY * width + srcX) * 4;
      const dstIdx = (y * newWidth + x) * 4;

      output[dstIdx] = pixels[srcIdx];
      output[dstIdx + 1] = pixels[srcIdx + 1];
      output[dstIdx + 2] = pixels[srcIdx + 2];
      output[dstIdx + 3] = pixels[srcIdx + 3];
    }
  }

  return { data: output, width: newWidth, height: newHeight };
}
